
import React, { useState } from 'react';
import SVGComponent from '../../SVG/SVGComponent';
import { SVG_ICONS, BOOKING_TYPE } from '../../../constants';
import validateFields from '../../../utils/validation';
import FilterStep from './FilterStep';
import ConfirmStep from './ConfirmStep';

/**
 * @module Modals/BulkBookUnbookModal
 */
/**
 * @typedef {object} props
 * @property {Array} competitions
 * @property {string} getTimezone - the app's currently selected timezone. The From/To date and time
 * fields are entered as wall-clock values in this timezone; the backend performs the UTC conversion.
 * @property {Function} cancel
 * @property {Function} bulkBookEvents
 * @property {Function} bulkUnbookEvents
 * @property {object} currentFilters - the Events page's currently applied filters, used both as the
 * modal's date defaults and as the source of the competition selection (via `leagueFilterKey`).
 * An empty/missing selection means "All competitions", matching the events page filter.
 * @property {string} [leagueFilterKey] - the currentFilters key holding the selected league/competition
 * ids array (`filterLeague[]` for soccer, `filterCompetitions[]` for tennis).
 */

const STEPS = {
  FILTER: 'filter',
  CONFIRM: 'confirm',
};

const splitDateTime = (value) => {
  if (!value) {
    return { date: '', time: '' };
  }
  const separator = value.includes('T') ? 'T' : ' ';
  const [date, time] = value.split(separator);
  return { date: date || '', time: time || '' };
};

const getDefaultDateTime = (currentFilters) => {
  const from = splitDateTime(currentFilters?.filterStartDate);
  const to = splitDateTime(currentFilters?.filterEndDate);
  return {
    fromDate: from.date,
    fromTime: from.time,
    toDate: to.date,
    toTime: to.time,
  };
};

const getSelectedCompetitions = (currentFilters, competitions, leagueFilterKey) => {
  const leagueIds = currentFilters?.[leagueFilterKey] || [];
  return leagueIds
    .map((leagueId) => competitions.find((c) => String(c.id) === String(leagueId)))
    .filter(Boolean);
};

/**
 * @param {props} props
 * @returns {view}
 */
const BulkBookUnbookModal = (props) => {
  const [step, setStep] = useState(STEPS.FILTER);
  const [dateTime, setDateTime] = useState(() => getDefaultDateTime(props.currentFilters));
  const [pendingBookingType, setPendingBookingType] = useState(null);
  const [errorSummary, setErrorSummary] = useState(null);

  const selectedCompetitions = getSelectedCompetitions(
    props.currentFilters, props.competitions, props.leagueFilterKey || 'filterLeague[]',
  );

  const changeDateTime = (field, value) => {
    setDateTime((prevDateTime) => ({ ...prevDateTime, [field]: value }));
    setErrorSummary(null);
  };

  const isFormComplete = () => Boolean(dateTime.fromDate && dateTime.fromTime);

  const chooseAction = (bookingType) => {
    const fromDateTime = `${dateTime.fromDate} ${dateTime.fromTime}`;
    const validationParams = [{
      label: 'From date',
      fieldName: 'fromDateTime',
      value: fromDateTime,
      rules: ['mandatory'],
    }];

    const hasToDateTime = dateTime.toDate && dateTime.toTime;
    if (bookingType === BOOKING_TYPE.UNBOOK && hasToDateTime) {
      const toDateTime = `${dateTime.toDate} ${dateTime.toTime}`;
      validationParams.push({
        label: 'To date',
        fieldName: 'toDateTime',
        value: toDateTime,
        rules: ['greaterThan'],
        comparatorValue: fromDateTime,
      });
    }

    validateFields(validationParams)
      .then(() => {
        setErrorSummary(null);
        setPendingBookingType(bookingType);
        setStep(STEPS.CONFIRM);
      })
      .catch((errors) => {
        setErrorSummary(errors);
      });
  };

  const backToFilter = () => {
    setStep(STEPS.FILTER);
  };

  const confirmAction = () => {
    const payload = {
      filterStartDate: `${dateTime.fromDate} ${dateTime.fromTime}`,
    };
    if (pendingBookingType === BOOKING_TYPE.UNBOOK && dateTime.toDate && dateTime.toTime) {
      payload.filterEndDate = `${dateTime.toDate} ${dateTime.toTime}`;
    }
    if (selectedCompetitions.length > 0) {
      payload.competition = selectedCompetitions.map((competition) => parseInt(competition.id, 10));
    }
    if (pendingBookingType === BOOKING_TYPE.BOOK) {
      props.bulkBookEvents(payload);
    } else {
      props.bulkUnbookEvents(payload);
    }
    props.cancel();
  };

  return (
    <div className="dialog dialog--selection">
      <div className="dialog__heading">
        <span className="dialog__title">
          {step === STEPS.CONFIRM ? `${BOOKING_TYPE[pendingBookingType]} All - Confirm` : 'Book/Unbook All'}
        </span>
        <SVGComponent
          onClick={props.cancel}
          className="icon-xxs  dialog__close"
          src={`${SVG_ICONS.utility}#close`}
        />
      </div>
      {step === STEPS.CONFIRM ? (
        <ConfirmStep
          dateTime={dateTime}
          selectedCompetitions={selectedCompetitions}
          pendingBookingType={pendingBookingType}
          timezone={props.getTimezone}
          backToFilter={backToFilter}
          confirmAction={confirmAction}
        />
      ) : (
        <FilterStep
          dateTime={dateTime}
          selectedCompetitions={selectedCompetitions}
          errorSummary={errorSummary}
          changeDateTime={changeDateTime}
          isFormComplete={isFormComplete}
          chooseAction={chooseAction}
        />
      )}
    </div>
  );
};

export default BulkBookUnbookModal;
